import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Link2, Trash2, Layers, Palette, ArrowUp, ArrowDown, Check } from "lucide-react";
import { useServiceCategories, CATEGORY_PALETTE, colorFor, solid, tint, UNCATEGORIZED } from "@/lib/categories";

const DEP_TYPES = [
  { v: "mandatory", l: "Obbligatorio (auto)" },
  { v: "included", l: "Incluso nel prezzo" },
  { v: "supplement", l: "Supplemento" },
  { v: "optional", l: "Facoltativo" },
];

let _uidSeq = 0;
const uid = () => `dep-${++_uidSeq}`;

// Swatch di colore selezionabile per la categoria.
function ColorSwatches({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="category-color-swatches">
      {CATEGORY_PALETTE.map((p) => (
        <button key={p.key} type="button" onClick={() => onChange(p.hsl)} title={p.key}
          data-testid={`swatch-${p.key}`}
          className="h-7 w-7 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110"
          style={{ backgroundColor: solid(p.hsl), borderColor: value === p.hsl ? "hsl(var(--foreground))" : "transparent" }}>
          {value === p.hsl && <Check className="h-3.5 w-3.5 text-white" />}
        </button>
      ))}
    </div>
  );
}

// Gestione categorie: crea / rinomina / ordina / colore / elimina.
function CategoryManager({ open, onOpenChange, categories, reload }) {
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(CATEGORY_PALETTE[0].hsl);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!newName.trim()) { toast.error("Nome categoria obbligatorio"); return; }
    setBusy(true);
    try { await api.post("/service-categories", { name: newName.trim(), color: newColor }); toast.success("Categoria creata"); setNewName(""); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  const rename = async (c, name) => { await api.put(`/service-categories/${c.id}`, { name }); await reload(); };
  const recolor = async (c, color) => { await api.put(`/service-categories/${c.id}`, { color }); await reload(); };
  const del = async (c) => { if (!window.confirm(`Eliminare la categoria "${c.name}"? I servizi collegati torneranno "Da categorizzare".`)) return; await api.delete(`/service-categories/${c.id}`); toast.success("Categoria eliminata"); await reload(); };
  const move = async (idx, dir) => {
    const arr = [...categories]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await api.post("/service-categories/reorder", { ordered_ids: arr.map((c) => c.id) });
    await reload();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="category-manager">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Gestione categorie</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {categories.map((c, idx) => (
            <Card key={c.id} className="p-3" style={{ borderLeft: `4px solid ${solid(c.color)}` }} data-testid={`cat-row-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="h-5 w-5 rounded-full shrink-0" style={{ backgroundColor: solid(c.color) }} />
                <Input defaultValue={c.name} onBlur={(e) => e.target.value.trim() && e.target.value !== c.name && rename(c, e.target.value.trim())}
                  className="h-9 flex-1 min-w-[140px]" data-testid={`cat-name-${c.id}`} />
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, -1)} disabled={idx === 0}><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, 1)} disabled={idx === categories.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(c)} data-testid={`cat-del-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-2"><ColorSwatches value={c.color} onChange={(hsl) => recolor(c, hsl)} /></div>
            </Card>
          ))}
          <Card className="p-3 border-dashed">
            <Label className="text-sm font-medium">Nuova categoria</Label>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nome categoria" className="h-9 flex-1 min-w-[140px]" data-testid="new-cat-name" />
              <Button onClick={create} disabled={busy} data-testid="new-cat-create"><Plus className="h-4 w-4 mr-1" /> Crea</Button>
            </div>
            <div className="mt-2"><ColorSwatches value={newColor} onChange={setNewColor} /></div>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Servizi() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [catMgr, setCatMgr] = useState(false);
  const { categories, colorByName, reload: reloadCats } = useServiceCategories();
  const empty = { name: "", category: "", price: 0, price_from: false, duration_min: 30, processing_min: 0, description: "", dependencies: [] };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const openEditor = (s) => { setEdit(s); setForm(s ? { ...empty, ...s } : empty); setPreview(null); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Nome obbligatorio"); return; }
    if (edit) await api.put(`/services/${edit.id}`, form); else await api.post("/services", form);
    toast.success("Servizio salvato"); setOpen(false); load();
  };
  const del = async (s) => { await api.delete(`/services/${s.id}`); toast.success("Eliminato"); load(); };

  const addDep = () => setForm((f) => ({ ...f, dependencies: [...(f.dependencies || []), { service_id: "", type: "mandatory", quantity: 1, _uid: uid() }] }));
  const updDep = (i, k, v) => setForm((f) => { const d = [...f.dependencies]; d[i] = { ...d[i], [k]: v }; return { ...f, dependencies: d }; });
  const rmDep = (i) => setForm((f) => ({ ...f, dependencies: f.dependencies.filter((_, x) => x !== i) }));

  const runPreview = async () => {
    if (edit) { const { data } = await api.post("/services/resolve", { service_ids: [edit.id], optional_ids: [] }); setPreview(data); }
  };

  // Ordine dei gruppi: categorie configurate (per 'order'), poi "Da categorizzare".
  const catNames = categories.map((c) => c.name);
  const groups = [...catNames, UNCATEGORIZED];
  const byCat = {};
  services.forEach((s) => {
    const key = s.category && catNames.includes(s.category) ? s.category : UNCATEGORIZED;
    (byCat[key] = byCat[key] || []).push(s);
  });

  return (
    <div>
      <PageHeader title="Servizi" subtitle="Catalogo servizi con categorie colorate, bundle e dipendenze">
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatMgr(true)} data-testid="manage-categories-button"><Palette className="h-4 w-4 mr-1" /> Categorie</Button>
          <Button onClick={() => openEditor(null)} data-testid="service-new-button"><Plus className="h-4 w-4 mr-1" /> Nuovo servizio</Button>
        </div>
      </PageHeader>

      <div className="space-y-6">
        {groups.map((cat) => {
          const list = byCat[cat];
          if (!list || !list.length) return null;
          const color = colorFor(colorByName, cat === UNCATEGORIZED ? null : cat);
          return (
            <div key={cat}>
              <h3 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: solid(color) }} />
                {cat} <span className="text-sm font-normal text-muted-foreground">({list.length})</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {list.map((s) => {
                  const c = colorFor(colorByName, s.category || null);
                  return (
                    <Card key={s.id} className="p-4 cursor-pointer hover:brightness-[0.98] transition" onClick={() => openEditor(s)}
                      data-testid={`service-card-${s.id}`}
                      style={{ borderLeft: `4px solid ${solid(c)}`, backgroundColor: tint(c, 0.07) }}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="font-medium flex items-center gap-2">{s.name}{(s.dependencies || []).length > 0 && <Layers className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" />}</div>
                        <div className="font-heading font-semibold shrink-0">{s.price_from ? "da " : ""}{eur(s.price)}</div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{s.duration_min}' {s.processing_min ? `+ ${s.processing_min}' posa` : ""}</div>
                      {(s.dependencies || []).length > 0 && <Badge variant="secondary" className="mt-2 text-[10px]"><Link2 className="h-3 w-3 mr-1" />{s.dependencies.length} dipendenze</Badge>}
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <CategoryManager open={catMgr} onOpenChange={setCatMgr} categories={categories} reload={async () => { await reloadCats(); load(); }} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-2xl">{edit ? "Modifica servizio" : "Nuovo servizio"}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={form.category || "__none__"} onValueChange={(v) => setForm({ ...form, category: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="service-category"><SelectValue placeholder="Da categorizzare" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Da categorizzare</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>
                        <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: solid(c.color) }} />{c.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Prezzo (€)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: +e.target.value })} data-testid="service-price" /></div>
              <div className="space-y-1.5"><Label>Durata (min)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: +e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Posa (min)</Label><Input type="number" value={form.processing_min} onChange={(e) => setForm({ ...form, processing_min: +e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between"><Label>Prezzo "a partire da"</Label><Switch checked={form.price_from} onCheckedChange={(v) => setForm({ ...form, price_from: v })} /></div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div className="border-t border-border pt-4">
              <div className="flex justify-between items-center mb-2"><Label className="text-base">Dipendenze / Bundle</Label><Button size="sm" variant="outline" onClick={addDep} data-testid="add-dependency"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi</Button></div>
              <div className="space-y-2">
                {(form.dependencies || []).map((dep, i) => (
                  <div key={dep._uid || dep.service_id || `dep-idx-${i}`} className="flex gap-2 items-center">
                    <Select value={dep.service_id} onValueChange={(v) => updDep(i, "service_id", v)}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Servizio" /></SelectTrigger>
                      <SelectContent>{services.filter((x) => x.id !== edit?.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={dep.type} onValueChange={(v) => updDep(i, "type", v)}>
                      <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{DEP_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min={1} value={dep.quantity || 1} onChange={(e) => updDep(i, "quantity", Math.max(1, +e.target.value || 1))} className="w-16" title="Quantità" data-testid={`dep-qty-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => rmDep(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              {edit && <Button variant="outline" size="sm" className="mt-3" onClick={runPreview} data-testid="service-preview">Anteprima calcolo bundle</Button>}
              {preview && <Card className="p-3 mt-2 text-sm"><div className="flex justify-between font-medium"><span>Totale bundle</span><span>{eur(preview.total_price)} · {preview.total_duration_min}'</span></div><div className="text-xs text-muted-foreground mt-1">{preview.lines.map((l) => `${l.quantity > 1 ? l.quantity + "x " : ""}${l.name} (${l.reason})`).join(", ")}</div></Card>}
            </div>

            <div className="flex gap-2">
              <Button className="flex-1" onClick={save} data-testid="service-save">Salva</Button>
              {edit && <Button variant="destructive" onClick={() => { del(edit); setOpen(false); }}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
